import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getApiKeysApi,
  createApiKeyApi,
  updateApiKeyApi,
  updateApiKeyStatusApi,
  deleteApiKeyApi,
  batchDeleteApiKeysApi,
  searchApiKeysApi,
} from '@/api/apiKeys'
import type { ApiKey } from '@/types'

export function useTokens(params?: { page?: number; page_size?: number }) {
  return useQuery({
    queryKey: ['tokens', params],
    queryFn: () => getApiKeysApi(params),
  })
}

export function useSearchTokens(keyword: string, enabled: boolean) {
  return useQuery({
    queryKey: ['tokens-search', keyword],
    queryFn: () => searchApiKeysApi(keyword),
    enabled: enabled && keyword.length > 0,
  })
}

export function useCreateToken() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createApiKeyApi,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens'] }),
  })
}

export function useUpdateToken() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateApiKeyApi,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens'] }),
  })
}

export function useUpdateTokenStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: number }) => updateApiKeyStatusApi(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens'] }),
  })
}

export function useDeleteToken() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteApiKeyApi,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens'] }),
  })
}

export function useBatchDeleteTokens() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => batchDeleteApiKeysApi(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens'] }),
  })
}

export type { ApiKey }
